import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Table,
  TableBody,
  ToggleButton,
  ToggleButtonGroup,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  Cell,
  Line,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Brush,
} from "recharts";
import { formatDate } from "../../utils/formatDate";
import {
  useMetricsUi,
  SectionHead,
  LegendItem,
  PAGE_FONT,
  MONO_FONT,
  headCellSx,
  textCellSx,
} from "../../components/metrics/metricsUi";
import { StatCard, cardGridSx } from "./forecastUi";
import ForecastModelSection, { DEMAND_TYPE_LABEL } from "./ForecastModelSection";
import useRunInsight from "./useRunInsight";
import InsightProgress, { EXPLANATION_STEPS } from "./InsightProgress";
import { isFilledRow, salesFillPoints } from "../../utils/sales-fill";
import InventoryLedgerSection from "./InventoryLedgerSection";
import { skippedWarehousesText } from "../../utils/warehouse-split";

const num = (v, dp = 0) =>
  v === null || v === undefined || v === "" || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-US", {
        minimumFractionDigits: dp,
        maximumFractionDigits: dp,
      });

// Shared styling for HTML fragments coming back from the forecast run
// (explanation, insight) — keeps both blocks visually consistent.
const richTextSx = (ui, colors) => ({
  p: "16px 18px",
  fontSize: 14,
  color: colors.textPrimary,
  textAlign: "left",
  "& *": { textAlign: "left" },
  // Trim the leading/trailing margin the fragment's first and last
  // blocks carry, so the padding around the text stays even.
  "& > *:first-of-type": { marginTop: 0 },
  "& > *:last-child": { marginBottom: 0 },
  "& h1, & h2, & h3, & h4": {
    fontFamily: PAGE_FONT,
    fontWeight: 700,
    fontSize: "15px",
    color: colors.textPrimary,
    mt: 1,
    mb: 0.5,
  },
  "& p": {
    fontFamily: PAGE_FONT,
    fontSize: "14px",
    color: colors.textSecondary,
    lineHeight: 1.7,
    mt: 0,
    mb: 1,
  },
  "& ul, & ol": { pl: 2.5, mb: 1 },
  "& li": {
    fontFamily: PAGE_FONT,
    fontSize: "14px",
    color: colors.textSecondary,
    lineHeight: 1.7,
    mb: 0.25,
  },
  "& strong, & b": {
    color: colors.textPrimary,
    fontWeight: 600,
  },
  "& a": { color: ui.accent },
  "& table": {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
    fontFamily: PAGE_FONT,
    mb: 1,
  },
  "& th": {
    bgcolor: ui.accentSoft,
    color: ui.accent,
    fontWeight: 700,
    p: "6px 10px",
    textAlign: "left",
    borderBottom: `2px solid ${ui.accent}30`,
  },
  "& td": {
    p: "5px 10px",
    color: colors.textSecondary,
    borderBottom: `1px solid ${colors.border}`,
  },
  "& code": {
    fontFamily: MONO_FONT,
    bgcolor: ui.surfaceAlt,
    borderRadius: "3px",
    px: 0.5,
    fontSize: "13px",
  },
});

// Chart shows this many of the most recent actuals leading into the forecast
// by default; "All" shows the run's whole sales history.
const HISTORY_POINTS = 18;
// Past this many bars the chart gets a range slider (opening on the latest
// ones) instead of squeezing every bar in, and drops the per-bar numbers.
const MAX_VISIBLE_POINTS = 40;
const PERIOD_WORD = { daily: "days", weekly: "weeks", monthly: "months" };

// One color per SKU when several are stacked in a single chart (Product
// view). Deliberately avoids the trend line's green.
const SKU_PALETTE = [
  "#2563EB",
  "#7C3AED",
  "#0891B2",
  "#D97706",
  "#DB2777",
  "#EA580C",
  "#4F46E5",
  "#0EA5E9",
  "#DC2626",
  "#C026D3",
];

// `historical_data` has no fixed schema server-side yet — read whichever of
// these the run actually populated.
// Demand-forecast-engine history rows carry their period start as `ds`.
const historicalDate = (row) => row?.date ?? row?.ds ?? row?.period ?? row?.month;
const historicalQty = (row) =>
  Number(
    row?.quantity ??
      row?.actual_qty ??
      row?.qty ??
      row?.value ??
      row?.demand ??
      row?.predicted_qty ??
      0,
  ) || 0;

const idOf = (w) => String(w?._id || w || "");

// `input.warehouse` may be a scalar, an array, or populated warehouse docs.
const selectedWarehouseIds = (run) => {
  const w = run?.input?.warehouse;
  const list = Array.isArray(w) ? w : w ? [w] : [];
  return list.map(idOf).filter(Boolean);
};

/**
 * Which forecast series to show for a run. The forecast is always run on all
 * orders and split across active warehouses (`run.allocation`); when the run
 * was filtered to specific warehouses, only their share is shown.
 *  scope "all"         — no warehouse filter: the full forecast
 *  scope "allocated"   — the selected warehouses' allocated share
 *  scope "unallocated" — warehouses were selected but the run has no split
 *                        for them (older run, or not an active warehouse):
 *                        the full forecast is shown, flagged as such
 */
const forecastView = (run) => {
  const full = run?.forecast_adjusted || [];
  const selected = selectedWarehouseIds(run);
  if (!selected.length) return { scope: "all", series: full, picked: [] };
  const picked = (run?.allocation?.warehouses || []).filter((w) =>
    selected.includes(idOf(w.warehouse)),
  );
  if (!picked.length) return { scope: "unallocated", series: full, picked };
  return {
    scope: "allocated",
    picked,
    series: full.map((f, i) => ({
      date: f.date,
      predicted_qty: picked.reduce(
        (s, w) => s + (Number(w.per_period?.[i]?.predicted_qty) || 0),
        0,
      ),
    })),
  };
};

const BASIS_NOTE = {
  sku: "this SKU's own orders",
  product:
    "orders across the whole product (this SKU had too few orders on its own)",
  even: "no order history with a usable zip — split evenly",
};

/**
 * Sums the warehouse split across one run, or several (a product's SKUs in
 * the "All SKUs" view), into table rows plus the facts behind them for the
 * explanation. Null when none of the runs was allocated.
 */
const summarizeAllocations = (runs) => {
  const allocated = (runs || []).filter((r) => r?.allocation);
  if (!allocated.length) return null;
  const single = allocated.length === 1 ? allocated[0].allocation : null;
  const periods = new Set();
  const rows = new Map();
  const skipped = new Map();
  allocated.forEach(({ allocation: a }) => {
    (a.skipped_warehouses || []).forEach((s) => skipped.set(idOf(s.warehouse), s));
    (a.warehouses || []).forEach((w) => {
      const id = idOf(w.warehouse);
      if (!rows.has(id)) {
        rows.set(id, { id, name: w.name, postal: w.postal, windowUnits: 0, pct: w.pct, total: 0, byDate: {} });
      }
      const row = rows.get(id);
      row.windowUnits += Number(w.units_in_window) || 0;
      row.total += Number(w.forecast_units) || 0;
      (w.per_period || []).forEach((p) => {
        const k = new Date(p.date).toISOString();
        periods.add(k);
        row.byDate[k] = (row.byDate[k] || 0) + (Number(p.predicted_qty) || 0);
      });
    });
  });
  const list = [...rows.values()];
  const grand = list.reduce((s, r) => s + r.total, 0);
  // Across several SKUs the share is of the summed allocated forecast — the
  // per-SKU window units can't be added up (thin SKUs reuse the product's).
  if (!single) list.forEach((r) => { r.pct = grand > 0 ? r.total / grand : 0; });
  return {
    single,
    reason: single?.reason || (list.length ? null : allocated[0].allocation.reason),
    periods: [...periods].sort(),
    rows: list.sort((a, b) => b.total - a.total),
    skipped: [...skipped.values()],
    grand,
  };
};

// Plain-language account of how the split was made (forecast question #4 —
// the allocation maths must be visible alongside the forecast).
const allocationExplanation = (summary) => {
  const { single } = summary;
  const lines = [];
  if (!summary.rows.length) {
    // "no active warehouse with a zip code" used to be stated unconditionally
    // here, but basis "none" also fires when active warehouses HAVE a zip
    // but none has a USPS zone chart yet (no_zone_chart) — that claim would
    // be false in that case. The skipped-warehouses line below (pushed
    // further down when summary.skipped.length) already states the real
    // per-warehouse reason, so the headline here stays reason-agnostic.
    lines.push(
      summary.skipped.length
        ? "This forecast wasn't split across warehouses — none of the active warehouses could be used (see below)."
        : "This forecast wasn't split across warehouses — no active warehouses were found for this client.",
    );
  } else if (single) {
    const w = single.window || {};
    lines.push(
      "The forecast was run on all orders, whichever warehouse shipped them, then split by where the demand comes from.",
    );
    lines.push(
      single.basis === "even"
        ? `There was ${BASIS_NOTE.even}.`
        : `Using ${num(single.total_units_in_window)} units from ${BASIS_NOTE[single.basis] || "past orders"} over the last ${w.months} months (${formatDate(w.from)} – ${formatDate(w.to)}), each order was assigned to the active warehouse with the lowest USPS shipping zone to its delivery zip. Each warehouse's share of those units is applied to every forecast period.`,
    );
    const ex = single.excluded || {};
    const parts = [];
    if (ex.non_us_units) parts.push(`${num(ex.non_us_units)} international`);
    if (ex.no_zip_units) parts.push(`${num(ex.no_zip_units)} with no zip`);
    if (ex.no_zone_units) {
      parts.push(`${num(ex.no_zone_units)} with no shipping zone to any warehouse`);
    }
    if (parts.length) lines.push(`Not counted: ${parts.join(", ")} units.`);
  } else {
    lines.push(
      "Totals across this product's SKUs. Each SKU was forecast on all orders and split by where its own demand comes from — open a SKU's tab to see its order window and units.",
    );
  }
  if (summary.skipped.length) {
    lines.push(`Not included: ${skippedWarehousesText(summary.skipped)}.`);
  }
  return lines;
};

const AllocationSection = ({ ui, summary, selectedIds, xLabel }) => {
  const { colors } = ui;
  const { single } = summary;
  return (
    <Box>
      <SectionHead title="Warehouse Allocation" />
      <Box sx={{ ...ui.card, p: "12px 16px", mb: summary.rows.length ? 1.25 : 0 }}>
        {allocationExplanation(summary).map((line, i) => (
          <Typography
            key={i}
            sx={{
              fontSize: 13,
              color: colors.textSecondary,
              lineHeight: 1.6,
              textAlign: "left",
            }}
          >
            {line}
          </Typography>
        ))}
      </Box>
      {summary.rows.length > 0 && (
        <TableContainer sx={{ ...ui.card, p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx(ui)}>Warehouse</TableCell>
                {single && <TableCell sx={headCellSx(ui)}>Order Units</TableCell>}
                <TableCell sx={headCellSx(ui)}>Allocation</TableCell>
                {summary.periods.map((p) => (
                  <TableCell key={p} sx={headCellSx(ui)}>
                    {xLabel(p)}
                  </TableCell>
                ))}
                <TableCell sx={headCellSx(ui)}>Total Allocated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.rows.map((r) => (
                <TableRow
                  key={r.id}
                  sx={
                    selectedIds.includes(r.id)
                      ? { backgroundColor: ui.accentSoft }
                      : undefined
                  }
                >
                  <TableCell sx={textCellSx(ui)}>
                    <strong>{r.name || "Warehouse"}</strong>
                    {r.postal && (
                      <Typography sx={{ fontSize: 10.5, color: colors.textSecondary }}>
                        {r.postal}
                      </Typography>
                    )}
                  </TableCell>
                  {single && (
                    <TableCell sx={textCellSx(ui)}>{num(r.windowUnits)}</TableCell>
                  )}
                  <TableCell sx={textCellSx(ui)}>{num(r.pct * 100, 1)}%</TableCell>
                  {summary.periods.map((p) => (
                    <TableCell key={p} sx={textCellSx(ui)}>
                      {num(r.byDate[p] || 0)}
                    </TableCell>
                  ))}
                  <TableCell sx={textCellSx(ui)}>
                    <strong>{num(r.total)}</strong>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

/**
 * Per-run x-axis labelling. `fmt` formats a date at the run's own cadence;
 * `historyDates(n)` back-computes n ISO dates stepping backward from the
 * first forecast period (for actuals with no parseable own date).
 */
function makeLabeler(run) {
  // Cadence: monthly buckets show the month name; anything finer (daily /
  // weekly) shows "12 Mar". Prefer the stored bucket type, fall back to the
  // gap between the first two points.
  const rows = run?.forecast_adjusted || [];
  const t = String(run?.input?.type || "").toLowerCase();
  let monthly;
  let bucketDays; // step size used to back-fill historical labels
  if (t === "monthly") {
    monthly = true;
    bucketDays = 30;
  } else if (t === "weekly") {
    monthly = false;
    bucketDays = 7;
  } else if (t === "daily") {
    monthly = false;
    bucketDays = 1;
  } else if (rows.length >= 2) {
    const gapDays =
      Math.abs(
        new Date(rows[1].date).getTime() - new Date(rows[0].date).getTime(),
      ) / 86400000;
    monthly = gapDays >= 20;
    bucketDays = gapDays || 30;
  } else {
    monthly = true;
    bucketDays = 30;
  }

  // Monthly labels always carry the year (not just when a single run's own
  // span happens to cross one) — with up to 18 historical points now shown,
  // a bare "Aug" is ambiguous almost by default, and in the stacked "All
  // SKUs" chart, each SKU used to decide this independently: a SKU whose
  // own horizon happened to cross a calendar year got "Aug 26" while a
  // shorter-horizon sibling contributing to the very same calendar month
  // got bare "Aug" — two different label strings for one month, splitting
  // what should be a single bar into two. Always showing the year removes
  // the inconsistency at the source, for both the single-run and stacked
  // charts (every run's `fmt` now formats a given month identically).
  //
  // forecast_adjusted dates are UTC-anchored bucket starts (e.g.
  // 2026-09-01T00:00:00.000Z). Format in UTC so the label doesn't shift a
  // month/day in browsers running behind or ahead of UTC.
  const fmt = (dateStr) => {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr ?? "");
    if (monthly) {
      return d.toLocaleDateString("en-US", {
        timeZone: "UTC",
        month: "short",
        year: "2-digit",
      });
    }
    return d.toLocaleDateString("en-US", {
      timeZone: "UTC",
      day: "numeric",
      month: "short",
    });
  };

  // `historical_data` has no fixed schema, so its date field isn't always
  // recognizable (see historicalDate below). Give every historical bar an
  // axis label anyway — the same way forecast bars get theirs — by
  // stepping backward from the first forecast period at this same
  // cadence, oldest first, right up to where the forecast begins.
  const historyDates = (count) => {
    if (!count) return [];
    const anchor = rows[0]?.date ? new Date(rows[0].date) : new Date();
    if (Number.isNaN(anchor.getTime())) return Array(count).fill("");
    const out = [];
    for (let i = count; i >= 1; i -= 1) {
      const d = new Date(anchor);
      if (monthly) d.setUTCMonth(d.getUTCMonth() - i);
      else d.setUTCDate(d.getUTCDate() - i * bucketDays);
      out.push(d.toISOString());
    }
    return out;
  };
  const historyLabels = (count) => historyDates(count).map((d) => (d ? fmt(d) : ""));

  return { fmt, historyDates, historyLabels };
}

/**
 * Metrics + projected-demand chart/table + config changes + explanation for
 * one forecast-data run. `stackRuns` (Product view): every SKU's run under the
 * same product — when it holds 2+, the chart stacks all of them per period
 * instead of showing just `run`'s own bars. `chartOnly` renders just that
 * chart (product-level view); `onSelectRun(run)` fires when a SKU's legend
 * entry or bar segment is clicked.
 */
/** Total forecast quantity and likely range (when every period has one). */
const forecastTotals = (run) => {
  const view = forecastView(run);
  const qty = view.series.reduce((s, row) => s + (Number(row.predicted_qty) || 0), 0);
  const rows = view.series.filter((r) => r.lower != null && r.upper != null);
  const range =
    rows.length && rows.length === view.series.length
      ? [
          rows.reduce((s, r) => s + (Number(r.lower) || 0), 0),
          rows.reduce((s, r) => s + (Number(r.upper) || 0), 0),
        ]
      : null;
  return { qty, range };
};

/**
 * One line for a collapsed Forecast Parameters header: the numbers a planner
 * acts on, so they're visible without opening it.
 */
export const forecastHeadline = (run) => {
  if (!run) return "";
  const m = run.metrics || {};
  const { qty } = forecastTotals(run);
  const model = run.forecast_model;
  const periods = run.input?.horizon
    ? `${num(run.input.horizon)} ${String(run.input.type || "").toLowerCase()} periods`
    : null;
  return [
    periods,
    `Forecast ${num(qty)} units${
      model?.event_uplift?.total_units ? ` (incl. ${num(model.event_uplift.total_units)} event uplift)` : ""
    }`,
    m.recommended_order_qty != null ? `Order ${num(m.recommended_order_qty)}` : null,
    m.reorder_by_date ? `reorder by ${formatDate(m.reorder_by_date)}` : null,
    model?.model_label
      ? `${model.model_label}${
          model.tested_error?.wape != null
            ? `, ${num(model.tested_error.wape, 1)}% tested error${model.event_uplift ? " (before uplift)" : ""}`
            : ""
        }`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
};

/** Forecast Summary + (demand-forecast engine) Forecast Method cards for one run. */
export function ForecastOverview({ run }) {
  if (!run) return null;
  const metrics = run.metrics || {};
  const modelInfo = run.forecast_model || null;
  const isDemandEngine = run.engine === "demand_forecast" || Boolean(modelInfo);
  const { qty: totalForecastQty, range: totalRange } = forecastTotals(run);
  // Event uplift the user added — the tested figures below don't include it.
  const upliftTotal = Number(modelInfo?.event_uplift?.total_units) || 0;
  return (
    <>
      {(
      <Box>
        <SectionHead title="Forecast Summary" />
        <Box sx={cardGridSx}>
          <StatCard label="On Hand" value={num(metrics.total_on_hand)} />
          {metrics.incoming_po_units != null ? (
            // Updated Forecast: stock on the way (open POs + net transfers),
            // which the order quantity already takes off.
            <StatCard
              label="Incoming (POs + Transfers)"
              value={num(metrics.total_in_transit)}
              hint={`${num(metrics.incoming_po_units)} on POs · ${
                metrics.incoming_transfer_units > 0 ? "+" : ""
              }${num(metrics.incoming_transfer_units)} transfers`}
            />
          ) : (
            <StatCard label="In Transit" value={num(metrics.total_in_transit)} />
          )}
          <StatCard
            label="Total Forecast Quantity"
            value={num(totalForecastQty)}
            hint={
              upliftTotal
                ? `incl. ${upliftTotal > 0 ? "+" : "−"}${num(Math.abs(upliftTotal))} event uplift`
                : undefined
            }
          />
          <StatCard
            label="Avg Daily Demand"
            value={num(metrics.avg_daily_demand, 2)}
          />
          <StatCard
            label="Lead Time (days)"
            value={num(metrics.lead_time_days)}
          />
          <StatCard
            label="Safety Stock"
            value={num(
              metrics.safety_stock_used ?? metrics.safety_stock_computed,
            )}
          />
          <StatCard label="Reorder Point" value={num(metrics.reorder_point)} />
          <StatCard
            label="Recommended Order Qty"
            value={num(metrics.recommended_order_qty)}
          />
          <StatCard
            label="Reorder By Date"
            value={
              metrics.reorder_by_date
                ? formatDate(metrics.reorder_by_date)
                : "—"
            }
          />
        </Box>
      </Box>
      )}

      {isDemandEngine && (
        <Box>
          <SectionHead title="Forecast Method" />
          <Box sx={cardGridSx}>
            <StatCard
              label={`Likely Range${modelInfo?.interval_level ? ` (${modelInfo.interval_level}%)` : ""}`}
              info="likely_range"
              value={totalRange ? `${num(totalRange[0])} – ${num(totalRange[1])}` : "—"}
            />
            <StatCard label="Model" value={modelInfo?.model_label || modelInfo?.model || "—"} />
            <StatCard
              label="Demand Type"
              info="demand_type"
              value={DEMAND_TYPE_LABEL[modelInfo?.demand_type] || modelInfo?.demand_type || "—"}
            />
            <StatCard
              label={upliftTotal ? "Tested Error (before uplift)" : "Tested Error (per unit sold)"}
              info="tested_error"
              value={
                modelInfo?.tested_error?.wape != null ? `${num(modelInfo.tested_error.wape, 1)}%` : "—"
              }
            />
            <StatCard
              label={'Beat "Same as Last Year" by'}
              info="beat_benchmark"
              value={modelInfo?.beat_benchmark_pct != null ? `${num(modelInfo.beat_benchmark_pct, 1)}%` : "—"}
            />
          </Box>
        </Box>
      )}

    </>
  );
}

export default function ForecastResultView({
  run,
  stackRuns,
  chartOnly = false,
  onSelectRun,
  // The Updated Forecast page shows Summary + Method inside its collapsed
  // Forecast Parameters panel instead (see ForecastOverview).
  hideOverview = false,
}) {
  const ui = useMetricsUi();
  const { colors } = ui;

  // Fixed palette for this chart (not theme-derived — these three exact
  // values were specified directly).
  const historicalColor = "#7C3AED"; // purple
  const forecastColor = "#2563EB"; // blue
  const upliftColor = "#F59E0B"; // amber — event uplift added by the user
  const estimatedColor = "#94A3B8"; // slate — sales fill (estimated, not real sales)
  const trendColor = "#15803D"; // green

  // Runs from the demand-forecast engine (admin Updated Forecast page) also
  // carry how the model was chosen and a likely range per period.
  const modelInfo = run?.forecast_model || null;

  // Explanation can be long; keep it collapsed until the user opts in.
  const [explanationOpen, setExplanationOpen] = useState(false);
  // "How this forecast is generated?" — the method choice, hidden until asked for.
  const [methodOpen, setMethodOpen] = useState(false);
  // Sales history on the chart: the last HISTORY_POINTS periods, or all.
  const [showAllHistory, setShowAllHistory] = useState(false);
  // Sales fill: the estimated periods sit at the start of the history, so a
  // run that has them opens on the whole history — they're never hidden.
  const salesFill = salesFillPoints(run?.forecast_model?.sales_fill);
  const hasFilledRows = Array.isArray(run?.historical_data) && run.historical_data.some(isFilledRow);
  useEffect(() => {
    if (hasFilledRows) setShowAllHistory(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?._id]);
  const historyLimit = showAllHistory ? Infinity : HISTORY_POINTS;
  const recentHistory = (rows) =>
    Array.isArray(rows)
      ? Number.isFinite(historyLimit)
        ? rows.slice(-historyLimit)
        : rows
      : [];
  // Insight + explanation — filled in live when the insight workflow finishes.
  const insightState = useRunInsight(run);
  const insightPending = insightState.status === "pending";
  // Only Updated Forecast runs can regenerate just the insight.
  const canRegenerate = run?.engine === "demand_forecast" || Boolean(modelInfo);

  const { xLabel, historyLabels } = useMemo(() => {
    const l = makeLabeler(run);
    return { xLabel: l.fmt, historyLabels: l.historyLabels };
  }, [run]);

  const view = useMemo(() => forecastView(run), [run]);
  // Selected warehouses' combined share — history is scaled by the same
  // share so it stays comparable with their allocated forecast.
  const historyShare =
    view.scope === "allocated"
      ? view.picked.reduce((s, w) => s + (Number(w.pct) || 0), 0)
      : 1;

  // Last few actuals leading into the forecast, then every forecasted
  // period — one continuous, chronological sequence.
  const historicalRows = useMemo(
    () => recentHistory(run?.historical_data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [run, historyLimit],
  );

  const chartData = useMemo(() => {
    // Prefer a real date on the row if it parses; otherwise fall back to the
    // computed, cadence-consistent label so a historical bar is never left
    // without an x-axis label.
    const fallbackLabels = historyLabels(historicalRows.length);
    const historyPoints = historicalRows.map((row, i) => {
      const raw = historicalDate(row);
      const parsed = raw != null ? new Date(raw) : null;
      const label =
        parsed && !Number.isNaN(parsed.getTime())
          ? xLabel(raw)
          : fallbackLabels[i];
      const qty = Math.round(historicalQty(row) * historyShare);
      // Filled (estimated) periods are their own series, never "Historical".
      return isFilledRow(row)
        ? { label, historical_qty: null, estimated_qty: qty, predicted_qty: null }
        : { label, historical_qty: qty, predicted_qty: null };
    });
    const forecastPoints = view.series.map((row) => {
      const lo = Number(row.lower);
      const hi = Number(row.upper);
      const total = Number(row.predicted_qty) || 0;
      // Event uplift the user added: drawn as its own segment on top of the
      // tested forecast, so the curve is never mistaken for the method's.
      const uplift = Number(row.event_uplift) || 0;
      const split = uplift > 0 && row.base_qty != null;
      return {
        label: xLabel(row.date),
        historical_qty: null,
        predicted_qty: split ? Number(row.base_qty) || 0 : total,
        event_uplift: split ? uplift : null,
        predicted_label: split ? null : total,
        uplift_label: split ? total : null,
        // Likely range (demand-forecast engine). Only on the unsplit
        // series — allocated rows don't carry bounds.
        ...(row.lower != null && row.upper != null && Number.isFinite(lo) && Number.isFinite(hi)
          ? { range: [Math.round(lo), Math.round(hi)] }
          : {}),
      };
    });
    const combined = [...historyPoints, ...forecastPoints];

    // Trend line: exactly the same value as each point's own bar (whichever
    // of historical/predicted it has) — so it always sits right at that
    // bar's top, never cutting through it. (A smoothed moving average was
    // tried here first, but it dips below spikes and visually intersects
    // the bar it's supposed to be tracing — wrong.)
    return combined.map((p) => ({
      ...p,
      trend: p.historical_qty ?? p.estimated_qty ?? (p.predicted_qty ?? 0) + (p.event_uplift || 0),
    }));
  }, [historicalRows, view, historyShare, xLabel, historyLabels]);

  // Stacked (Product view) chart: one stack per period, one segment per SKU.
  // Actuals and forecast periods from every SKU are merged onto one shared
  // timeline by their axis label; `trend` is the stack's total.
  const stacked = useMemo(() => {
    if (!Array.isArray(stackRuns) || stackRuns.length < 2) return null;
    const series = stackRuns.map((r, i) => ({
      key: `sku_${i}`,
      name: r?.sku || `SKU ${i + 1}`,
      color: SKU_PALETTE[i % SKU_PALETTE.length],
    }));
    const byLabel = new Map();
    const put = (iso, label, isForecast, key, qty, isEstimated = false) => {
      const ts = new Date(iso).getTime();
      let row = byLabel.get(label);
      if (!row) {
        row = { label, ts: Number.isNaN(ts) ? Infinity : ts, hasForecast: false };
        byLabel.set(label, row);
      } else if (!Number.isNaN(ts) && ts < row.ts) {
        row.ts = ts;
      }
      if (isForecast) row.hasForecast = true;
      if (isEstimated) row.hasEstimate = true;
      row[key] = (row[key] || 0) + qty;
    };
    stackRuns.forEach((r, i) => {
      const { fmt, historyDates } = makeLabeler(r);
      const rv = forecastView(r);
      const share =
        rv.scope === "allocated"
          ? rv.picked.reduce((s, w) => s + (Number(w.pct) || 0), 0)
          : 1;
      const hist = recentHistory(r?.historical_data);
      const fallback = historyDates(hist.length);
      hist.forEach((h, j) => {
        const raw = historicalDate(h);
        const parsed = raw != null ? new Date(raw) : null;
        const iso =
          parsed && !Number.isNaN(parsed.getTime())
            ? parsed.toISOString()
            : fallback[j];
        put(iso, fmt(iso), false, series[i].key, Math.round(historicalQty(h) * share), isFilledRow(h));
      });
      rv.series.forEach((f) => {
        // Event uplift the user added: each SKU's segment shows its tested
        // forecast, and the uplift of every SKU sits on top as one segment.
        const uplift = Number(f.event_uplift) || 0;
        if (uplift > 0 && f.base_qty != null) {
          put(f.date, fmt(f.date), true, series[i].key, Number(f.base_qty) || 0);
          put(f.date, fmt(f.date), true, "event_uplift", uplift);
        } else {
          put(f.date, fmt(f.date), true, series[i].key, Number(f.predicted_qty) || 0);
        }
      });
    });
    const data = [...byLabel.values()]
      .sort((a, b) => a.ts - b.ts)
      .map((row) => ({
        ...row,
        isHistory: !row.hasForecast,
        trend: series.reduce((sum, sr) => sum + (row[sr.key] || 0), 0) + (row.event_uplift || 0),
      }));
    const hasUplift = data.some((row) => row.event_uplift > 0);
    const hasEstimate = data.some((row) => row.hasEstimate);
    return { series, data, hasUplift, hasEstimate };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackRuns, historyLimit]);

  const plotData = stacked ? stacked.data : chartData;
  // Longest sales history among the shown run(s) — the "All" option's size.
  const totalHistory = Math.max(
    0,
    ...(stacked ? stackRuns : [run]).map((r) =>
      Array.isArray(r?.historical_data) ? r.historical_data.length : 0,
    ),
  );
  const periodWord =
    PERIOD_WORD[String(run?.input?.type || "").toLowerCase()] || "periods";
  const longChart = plotData.length > MAX_VISIBLE_POINTS;
  const hasUplift = !stacked && chartData.some((p) => p.event_uplift > 0);
  const denseAxis = plotData.length > 14;
  const hasRange = !stacked && chartData.some((p) => Array.isArray(p.range));

  // Warehouse split to show: this run's own, or — in the product-level
  // (stacked) view — every SKU's allocation summed per warehouse.
  const allocationSummary = useMemo(
    () => summarizeAllocations(stacked ? stackRuns : [run]),
    [stacked, stackRuns, run],
  );

  const tooltipStyle = {
    backgroundColor: colors.bgPaper,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    color: colors.textPrimary,
    fontSize: 12,
  };
  const axisTick = {
    fill: colors.textPrimary,
    fontSize: 10.5,
    fontFamily: MONO_FONT,
  };

  if (!run) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {!chartOnly && !hideOverview && <ForecastOverview run={run} />}

      {!chartOnly && insightPending && (
        <Box>
          <SectionHead title="DropSkip Insight" />
          <InsightProgress startedAt={insightState.startedAt || run?.createdAt} ui={ui} />
        </Box>
      )}

      {!chartOnly && !insightPending && insightState.insight && (
        <Box>
          <SectionHead title="DropSkip Insight" />
          <Box
            sx={{ ...ui.card, ...richTextSx(ui, colors) }}
            dangerouslySetInnerHTML={{ __html: insightState.insight }}
          />
        </Box>
      )}

      {!chartOnly && insightState.status === "error" && !insightState.insight && (
        <Box>
          <SectionHead title="DropSkip Insight" />
          <Box
            sx={{
              ...ui.card,
              p: "14px 18px",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 20, color: ui.warning }} />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ color: colors.textPrimary, fontSize: 13, fontWeight: 600 }}>
                {insightState.error || "The insight could not be generated for this forecast."}
              </Typography>
              {canRegenerate && (
                <Typography sx={{ color: colors.textSecondary, fontSize: 12 }}>
                  Regenerate it to write the insight and explanation again — the forecast itself stays the same.
                </Typography>
              )}
            </Box>
            {canRegenerate && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AutorenewIcon />}
                onClick={insightState.regenerate}
                disabled={insightState.regenerating}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {insightState.regenerating ? "Starting…" : "Regenerate insight"}
              </Button>
            )}
          </Box>
        </Box>
      )}

      <Box>
        <SectionHead
          title="Forecasted Units"
          subtitle={
            view.scope === "allocated"
              ? `${view.picked.map((w) => w.name || "Warehouse").join(", ")} — ${num(
                  historyShare * 100,
                  1,
                )}% of total demand (history scaled to the same share)`
              : view.scope === "unallocated"
                ? "No warehouse split for the selected warehouse(s) — showing the forecast across all warehouses"
                : undefined
          }
          right={
            plotData.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                {stacked ? (
                  <>
                    {stacked.series.map((sr, i) => (
                      <Box
                        key={sr.key}
                        onClick={() => onSelectRun?.(stackRuns[i])}
                        sx={{
                          cursor: onSelectRun ? "pointer" : "default",
                        }}
                      >
                        <LegendItem color={sr.color} label={sr.name} />
                      </Box>
                    ))}
                    {stacked.hasUplift && (
                      <LegendItem color={upliftColor} label="Event uplift (added)" />
                    )}
                    {stacked.hasEstimate && (
                      <LegendItem color={`${estimatedColor}66`} label="Faint: estimated (sales fill)" />
                    )}
                    <LegendItem color={trendColor} label="Total" line />
                  </>
                ) : (
                  <>
                    {historicalRows.some((r) => !isFilledRow(r)) && (
                      <LegendItem color={historicalColor} label="Historical" />
                    )}
                    {historicalRows.some(isFilledRow) && (
                      <LegendItem color={estimatedColor} label="Estimated (sales fill)" />
                    )}
                    <LegendItem color={forecastColor} label="Predicted Qty" />
                    {hasUplift && (
                      <LegendItem color={upliftColor} label="Event uplift (added)" />
                    )}
                    {hasRange && (
                      <LegendItem color={`${forecastColor}55`} label="Likely range" />
                    )}
                    <LegendItem color={trendColor} label="Trend" line />
                  </>
                )}
              </Box>
            ) : undefined
          }
        />
        {!stacked && salesFill.points.length > 0 && (
          <Box
            role="note"
            data-testid="sales-fill-notice"
            sx={{
              ...ui.card,
              p: "10px 16px",
              mb: 1.25,
              textAlign: "left",
              borderLeft: `4px solid ${salesFill.applied ? estimatedColor : colors.border}`,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary, mb: 0.5, textAlign: "left" }}>
              {salesFill.title}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {salesFill.points.map((t) => (
                <Typography key={t} component="li" sx={{ fontSize: 12.5, color: colors.textPrimary, lineHeight: 1.6, textAlign: "left" }}>
                  {t}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
        {plotData.length > 0 ? (
          <Box sx={{ ...ui.card, p: "16px 12px 8px" }}>
            {totalHistory > HISTORY_POINTS && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 1,
                  px: 0.5,
                  mb: 1,
                }}
              >
                <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                  Sales history
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={showAllHistory ? "all" : "recent"}
                  onChange={(_, v) => v && setShowAllHistory(v === "all")}
                  aria-label="Sales history shown"
                  sx={{
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      fontSize: 12,
                      py: 0.25,
                      px: 1.25,
                      color: colors.textSecondary,
                      borderColor: colors.border,
                    },
                    "& .Mui-selected": {
                      color: `${ui.accent} !important`,
                      backgroundColor: `${ui.accentSoft} !important`,
                    },
                  }}
                >
                  <ToggleButton value="recent">
                    Last {HISTORY_POINTS} {periodWord}
                  </ToggleButton>
                  <ToggleButton value="all">
                    All {num(totalHistory)} {periodWord}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
            <Box sx={{ width: "100%", height: longChart ? 350 : 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={plotData}
                  margin={{ top: 22, right: 12, bottom: 4, left: 0 }}
                  barCategoryGap="22%"
                >
                  <defs>
                    <pattern id="salesFillHatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                      <rect width="6" height="6" fill={`${estimatedColor}33`} />
                      <line x1="0" y1="0" x2="0" y2="6" stroke={estimatedColor} strokeWidth="2.5" />
                    </pattern>
                  </defs>
                  <CartesianGrid
                    stroke={colors.gridLine}
                    strokeDasharray="3 3"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={{ stroke: colors.border }}
                    tick={axisTick}
                    interval={0}
                    // Weekly/daily charts have ~30 bars: flat labels are wider
                    // than a bar slot, overlap and drift off their bars. Angle
                    // them so each one ends exactly under its own bar.
                    angle={denseAxis ? -40 : 0}
                    textAnchor={denseAxis ? "end" : "middle"}
                    height={denseAxis ? 52 : 30}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={axisTick}
                    width={44}
                    allowDecimals={false}
                  />
                  <RechartsTooltip
                    contentStyle={tooltipStyle}
                    cursor={{ fill: `${forecastColor}14` }}
                    formatter={(v, name) => [
                      Array.isArray(v) ? `${num(v[0])} – ${num(v[1])}` : num(v),
                      name,
                    ]}
                  />
                  {hasRange && (
                    <Area
                      dataKey="range"
                      name="Likely range"
                      stroke="none"
                      fill={forecastColor}
                      fillOpacity={0.14}
                      isAnimationActive={false}
                      connectNulls={false}
                    />
                  )}
                  {stacked ? (
                    // Product view: one segment per SKU, stacked per period.
                    // Actual periods are drawn lighter than forecast periods.
                    <>
                    {stacked.series.map((sr, i) => (
                      <Bar
                        key={sr.key}
                        dataKey={sr.key}
                        name={sr.name}
                        stackId="period"
                        fill={sr.color}
                        radius={
                          i === stacked.series.length - 1 && !stacked.hasUplift
                            ? [4, 4, 0, 0]
                            : 0
                        }
                        maxBarSize={64}
                        cursor={onSelectRun ? "pointer" : "default"}
                        onClick={() => onSelectRun?.(stackRuns[i])}
                        isAnimationActive
                        animationBegin={i * 60}
                        animationDuration={650}
                        animationEasing="ease-out"
                      >
                        {stacked.data.map((row, idx) => (
                          <Cell
                            key={idx}
                            // Estimated (sales fill) periods: faintest.
                            fillOpacity={row.hasEstimate ? 0.18 : row.isHistory ? 0.45 : 1}
                          />
                        ))}
                      </Bar>
                    ))}
                    {stacked.hasUplift && (
                      // Every SKU's event uplift together, on top of the stack.
                      <Bar
                        dataKey="event_uplift"
                        name="Event uplift (added)"
                        stackId="period"
                        fill={upliftColor}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={64}
                        isAnimationActive
                        animationBegin={stacked.series.length * 60}
                        animationDuration={650}
                        animationEasing="ease-out"
                      >
                        {!longChart && (
                          <LabelList
                            dataKey="event_uplift"
                            position="insideTop"
                            formatter={(v) => (v ? `+${num(v)}` : "")}
                            style={{
                              fill: "#3B2A00",
                              fontSize: 10,
                              fontWeight: 600,
                              fontFamily: MONO_FONT,
                            }}
                          />
                        )}
                      </Bar>
                    )}
                    </>
                  ) : (
                    <>
                      {/* Last few actuals, in purple, and the forecasted
                          periods, in blue, share one stackId — every row only
                          ever has a value for one of
                          the two, so nothing actually stacks; this just keeps
                          both series in the SAME bar slot per category instead
                          of Recharts splitting the category width into a 2-bar
                          cluster and rendering each one off-center. */}
                      <Bar
                        dataKey="historical_qty"
                        name="Historical"
                        stackId="period"
                        fill={historicalColor}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={64}
                        isAnimationActive
                        animationBegin={0}
                        animationDuration={650}
                        animationEasing="ease-out"
                      >
                        {!longChart && (
                          <LabelList
                          dataKey="historical_qty"
                          position="top"
                          formatter={(v) => (v == null ? "" : num(v))}
                          style={{
                            fill: colors.textPrimary,
                            fontSize: 10.5,
                            fontFamily: MONO_FONT,
                          }}
                        />
                        )}
                      </Bar>
                      {historicalRows.some(isFilledRow) && (
                        <Bar
                          dataKey="estimated_qty"
                          name="Estimated (sales fill, not real sales)"
                          stackId="period"
                          fill="url(#salesFillHatch)"
                          stroke={estimatedColor}
                          strokeDasharray="3 2"
                          radius={[4, 4, 0, 0]}
                          maxBarSize={64}
                          isAnimationActive
                          animationBegin={60}
                          animationDuration={650}
                          animationEasing="ease-out"
                        >
                          {!longChart && (
                            <LabelList
                              dataKey="estimated_qty"
                              position="top"
                              formatter={(v) => (v == null ? "" : `~${num(v)}`)}
                              style={{ fill: colors.textSecondary, fontSize: 10.5, fontFamily: MONO_FONT }}
                            />
                          )}
                        </Bar>
                      )}
                      <Bar
                        dataKey="predicted_qty"
                        name="Predicted Qty"
                        stackId="period"
                        fill={forecastColor}
                        radius={[4, 4, 0, 0]}
                        maxBarSize={64}
                        isAnimationActive
                        animationBegin={120}
                        animationDuration={650}
                        animationEasing="ease-out"
                      >
                        {!longChart && (
                          <LabelList
                          dataKey="predicted_label"
                          position="top"
                          formatter={(v) => (v == null ? "" : num(v))}
                          style={{
                            fill: colors.textPrimary,
                            fontSize: 10.5,
                            fontFamily: MONO_FONT,
                          }}
                        />
                        )}
                      </Bar>
                      {hasUplift && (
                        <Bar
                          dataKey="event_uplift"
                          name="Event uplift (added)"
                          stackId="period"
                          fill={upliftColor}
                          radius={[4, 4, 0, 0]}
                          maxBarSize={64}
                          isAnimationActive
                          animationBegin={240}
                          animationDuration={650}
                          animationEasing="ease-out"
                        >
                          {!longChart && (
                            <LabelList
                              dataKey="event_uplift"
                              position="insideTop"
                              formatter={(v) => (v ? `+${num(v)}` : "")}
                              style={{
                                fill: "#3B2A00",
                                fontSize: 10,
                                fontWeight: 600,
                                fontFamily: MONO_FONT,
                              }}
                            />
                          )}
                          {!longChart && (
                            <LabelList
                              dataKey="uplift_label"
                              position="top"
                              formatter={(v) => (v == null ? "" : num(v))}
                              style={{
                                fill: colors.textPrimary,
                                fontSize: 10.5,
                                fontFamily: MONO_FONT,
                              }}
                            />
                          )}
                        </Bar>
                      )}
                    </>
                  )}
                  {/* Trend line over both, on top of the bars — a smooth
                      glowing curve with a marker dot at every point. The
                      glow is a plain CSS drop-shadow on the line's own path
                      (not a second overlaid Line), so it doesn't add a
                      duplicate row to the tooltip. Draws in just after the
                      bars finish growing. */}
                  <Line
                    type="natural"
                    dataKey="trend"
                    name={stacked ? "Total" : "Trend"}
                    stroke={trendColor}
                    strokeWidth={2}
                    dot={
                      longChart
                        ? false
                        : {
                            r: 3.5,
                            fill: trendColor,
                            stroke: colors.bgPaper,
                            strokeWidth: 1.5,
                          }
                    }
                    activeDot={{ r: 5 }}
                    isAnimationActive
                    animationBegin={500}
                    animationDuration={500}
                    animationEasing="ease-out"
                    style={{
                      filter: `drop-shadow(0 0 3px ${trendColor}80)`,
                    }}
                  >
                    {stacked && !longChart && (
                      <LabelList
                        dataKey="trend"
                        position="top"
                        offset={10}
                        formatter={(v) => (v == null ? "" : num(v))}
                        style={{
                          fill: colors.textPrimary,
                          fontSize: 10.5,
                          fontFamily: MONO_FONT,
                        }}
                      />
                    )}
                  </Line>
                  {longChart && (
                    // Range slider: opens on the latest periods (history
                    // leading into the forecast); drag to see earlier sales.
                    <Brush
                      key={`${plotData.length}-${showAllHistory}`}
                      dataKey="label"
                      height={22}
                      travellerWidth={8}
                      startIndex={Math.max(0, plotData.length - MAX_VISIBLE_POINTS)}
                      endIndex={plotData.length - 1}
                      stroke={ui.accent}
                      fill={colors.bgPaper}
                      tickFormatter={() => ""}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        ) : (
          <Box sx={{ ...ui.card, p: 2.5 }}>
            <Typography sx={{ color: colors.textSecondary, fontSize: 13 }}>
              No projected demand values were stored for this run.
            </Typography>
          </Box>
        )}
      </Box>

      {!chartOnly && modelInfo && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              onClick={() => setMethodOpen((o) => !o)}
              endIcon={methodOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              aria-expanded={methodOpen}
              sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, px: 0.5 }}
            >
              Why this forecast method?
            </Button>
          </Box>
          <Collapse in={methodOpen} unmountOnExit>
            <Box sx={{ mt: 1 }}>
              <ForecastModelSection model={modelInfo} defaultDetailsOpen />
            </Box>
          </Collapse>
        </Box>
      )}

      {allocationSummary && (
        <AllocationSection
          ui={ui}
          summary={allocationSummary}
          selectedIds={selectedWarehouseIds(run)}
          xLabel={xLabel}
        />
      )}

      {!chartOnly && !stacked && run.ledger && (
        <InventoryLedgerSection ledger={run.ledger} sku={run.sku} />
      )}

      {!chartOnly &&
        Array.isArray(run.inventory_supplier_config_changes) &&
        run.inventory_supplier_config_changes.length > 0 && (
          <Box>
            <SectionHead title="Suggested Configuration Changes" />
            <TableContainer sx={{ ...ui.card, p: 0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx(ui)}>Field</TableCell>
                    <TableCell sx={headCellSx(ui)}>Current</TableCell>
                    <TableCell sx={headCellSx(ui)}>Suggested</TableCell>
                    <TableCell sx={headCellSx(ui)}>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {run.inventory_supplier_config_changes.map((c, i) => (
                    <TableRow key={i}>
                      <TableCell sx={textCellSx(ui)}>{c.field}</TableCell>
                      <TableCell sx={textCellSx(ui)}>
                        {c.current_value}
                      </TableCell>
                      <TableCell sx={textCellSx(ui)}>
                        {c.suggested_value}
                      </TableCell>
                      <TableCell sx={textCellSx(ui)}>{c.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

      {!chartOnly && insightPending && (
        // Written in the same workflow run as the insight, so it shares its
        // timing (same start, same estimate) — the two bars move together.
        <InsightProgress
          startedAt={insightState.startedAt || run?.createdAt}
          ui={ui}
          title="Writing the Explanation"
          steps={EXPLANATION_STEPS}
          label="Explanation progress"
        />
      )}

      {!chartOnly && !insightPending && insightState.explanation && (
        // Single bordered card: header bar + collapsible body share one
        // border / background so there is no seam or off-colour strip.
        <Box sx={{ ...ui.card, overflow: "hidden" }}>
          {/* Header bar: click anywhere to toggle. */}
          <Box
            onClick={() => setExplanationOpen((o) => !o)}
            sx={{
              p: "10px 12px 10px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              userSelect: "none",
              borderBottom: explanationOpen
                ? `1px solid ${colors.border}`
                : "none",
            }}
          >
            <Typography
              sx={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}
            >
              Explanation
            </Typography>
            <Button
              size="small"
              component="span"
              endIcon={
                explanationOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />
              }
              sx={{
                textTransform: "none",
                color: colors.textSecondary,
                fontSize: 12,
                pointerEvents: "none",
              }}
            >
              {explanationOpen ? "Hide" : "Show"}
            </Button>
          </Box>
          {/* Explanation is stored as an HTML fragment. */}
          <Collapse in={explanationOpen} unmountOnExit>
            <Box
              sx={richTextSx(ui, colors)}
              dangerouslySetInnerHTML={{ __html: insightState.explanation }}
            />
          </Collapse>
        </Box>
      )}
    </Box>
  );
}
